/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { parseDate } from '@internationalized/date';

import { getInitialState, VISUAL_SLICE_NAME } from './constants';
import { OwnerFilterItem, RangeValue } from './types';
import {
  convertStringToFullTextSearch,
  isRelativeDateRange,
  parseAndValidateDates,
  setDefaultUser,
  setUserValue,
  toRelativeDateRange,
} from './utils';
import * as thunks from './thunks';
import { types } from '../../../../web-ui-jobs/src/ducks/filterBar';
import { BuildStatus } from '../../graphql/__generated__/graphql';

export const filterBarSlice = createSlice({
  name: VISUAL_SLICE_NAME,
  initialState: getInitialState(),
  reducers: {
    // Date
    setDate: (state, action: PayloadAction<RangeValue<string>>) => {
      const { start, end } = action.payload;

      const relativeRange = toRelativeDateRange({
        start: parseDate(start),
        end: parseDate(end),
      });

      if (relativeRange) {
        state.date = {
          start: relativeRange,
          end: '',
        };
      } else {
        state.date = {
          start: action.payload.start.toString(),
          end: action.payload.end.toString(),
        };
      }
      state.page.value = getInitialState().page.value;
    },
    // Owner
    setOwner: (state, action: PayloadAction<OwnerFilterItem | null>) => {
      const owner = action.payload;
      if (owner) {
        setUserValue(state, owner);
      } else {
        setDefaultUser(state);
      }
      state.page.value = getInitialState().page.value;
    },
    setOwnerSearch: (state, action: PayloadAction<string | null>) => {
      state.owner.searchValue = action.payload || null;
    },
    // Name
    setBuildNameValue: (state, action: PayloadAction<string>) => {
      state.buildName.value = action.payload;
    },
    setBuildNameSearchValue: (state, action: PayloadAction<string | null>) => {
      state.buildName.searchValue = action.payload || null;
      state.page.value = getInitialState().page.value;
    },
    // Status
    setStatus: (state, action: PayloadAction<BuildStatus[]>) => {
      state.status.value = action.payload;
      state.page.value = getInitialState().page.value;
    },
    // Project
    setProject: (state, action: PayloadAction<string | null>) => {
      state.project.value = action.payload;
      state.page.value = getInitialState().page.value;
    },
    setProjectSearch: (state, action: PayloadAction<string | null>) => {
      state.project.searchValue = action.payload || null;
    },
    // Branch
    setBranch: (state, action: PayloadAction<string | null>) => {
      state.branch.value = action.payload;
    },
    setBranchSearch: (state, action: PayloadAction<string | null>) => {
      state.branch.searchValue = action.payload || null;
    },
    // Page
    setPage: (state, action: PayloadAction<number>) => {
      state.page.value = action.payload;
    },
    // All / global
    resetFilters: (state) => {
      const { date, buildName, page } = getInitialState();

      setDefaultUser(state);

      state.date.start = date.start;
      state.date.end = date.end;

      state.buildName = buildName;

      state.status.value = [];

      state.project.value = null;
      state.project.searchValue = null;

      state.branch.value = null;
      state.branch.searchValue = null;
      state.page.value = page.value;
    },
  },
  extraReducers: (builder) => {
    builder
      // Owner
      .addCase(thunks.initFilters.pending, (state, action) => {
        state.init = action.meta.requestStatus;
      })
      .addCase(thunks.initFilters.fulfilled, (state, action) => {
        const { user, params, loadedUser } = action.payload;

        // Prefill default values
        state.owner.items = [
          {
            label: `My tests (${user?.username ?? ''})`,
            key: user.user_id as string,
            type: 'user',
            static: true,
          },
          {
            label: 'My organization',
            key: user.organization.id,
            type: 'organization',
            static: true,
          },
          // If we've found a result for a user or a team, then preload that value
          ...(loadedUser ? [
            loadedUser,
          ] : []),
        ];

        if (params.ownerId && params.ownerType) {
          const foundItem = state.owner.items
            .find((_user) => (
              _user.key === params.ownerId &&
              _user.type === params.ownerType
            ));

          if (foundItem) {
            setUserValue(state, foundItem);
          }
        }

        if (!state.owner.value) {
          setDefaultUser(state);
        }

        // Parse & unserialize date
        const isRelative = isRelativeDateRange(params?.start);

        if (isRelative && params?.start) {
          // Parse relative date from params if present
          state.date.start = params.start;
          state.date.end = '';
        } else if (params?.start && params?.end) {
          // Else, if standard dates validate their ranges and box them in our allowable range

          try {
            const { start, end } = parseAndValidateDates({ start: params.start, end: params.end });
            state.date.start = start.toString();
            state.date.end = end.toString();
          } catch {
            state.date.start = types.RelativeDateRanges.last7Days;
            state.date.end = '';
          }
        }

        const buildName = params?.buildName;
        if (buildName) {
          state.buildName.value = buildName;
          state.buildName.searchValue = convertStringToFullTextSearch(buildName);
        }

        const statuses = params?.status;
        if (statuses && statuses.length) {
          const statusValues = Object.values(BuildStatus);
          state.status.value = <BuildStatus[]>statuses.filter(
            (status: BuildStatus) => statusValues.includes(status),
          );
        }

        state.project.value = params.project ?? null;
        state.branch.value = params.branch ?? null;
        state.page.value = params.page ?? state.page.value;

        state.init = 'succeeded';
      })
      .addCase(thunks.loadOwners.pending, (state) => {
        state.owner.loading = true;
      })
      .addCase(thunks.loadOwners.fulfilled, (state, action) => {
        const users = action.payload;
        state.owner.loading = false;

        const staticItems = state.owner.items
          .filter((owner) => owner.static === true)
          .concat(state.owner.items.filter((item) => item.key === state.owner.value))
          .filter((item, index, all) => (
            all.findIndex((_item) => _item.key === item.key) === index),
          );
        const staticUserIds = staticItems.map((user) => user.key);
        state.owner.items = staticItems.concat(
          users.filter((user) => staticUserIds.indexOf(user.key) === -1),
        );
      })
      .addCase(thunks.selectOwnerByUserId.pending, (state) => {
        state.owner.loading = true;
      })
      .addCase(thunks.selectOwnerByUserId.fulfilled, (state, action) => {
        state.owner.loading = false;
        const selectedOwner = action.payload;

        if (selectedOwner) {
          const localUser = state.owner.items.find((owner) => (
            owner.key === selectedOwner.key &&
            owner.type === 'user'
          ));

          if (!localUser) {
            state.owner.items = state.owner.items.concat(selectedOwner);
          }

          setUserValue(state, selectedOwner);
        }
      })
      // BuildName
      .addCase(thunks.updateBuildNameSearch.fulfilled, (state, action) => {
        state.buildName.searchValue = action.payload;
        state.page.value = getInitialState().page.value;
      });
  },
});
